import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionsProvider, usePermissions } from './contexts/PermissionsContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import Login from './pages/Login';
import Register from './pages/Register';
import ManagerDashboard from './pages/ManagerDashboard';
import MyDay from './pages/MyDay';
import KanbanBoard from './pages/KanbanBoard';
import TaskTable from './pages/TaskTable';
import DeveloperBoard from './pages/DeveloperBoard';
import UIUXDesignQueue from './pages/UIUXDesignQueue';
import ManagerApproval from './pages/ManagerApproval';
import UserTypesManagement from './pages/UserTypesManagement';
import UserManagement from './pages/UserManagement';
import MyProfile from './pages/MyProfile';
import ActivityLog from './pages/ActivityLog';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import RBACDebug from './pages/RBACDebug';
import SocialMediaLayout from './pages/SocialMedia/SocialMediaLayout';
import SocialMediaDashboard from './pages/SocialMedia/Dashboard';
import PostsTable from './pages/SocialMedia/PostsTable';
import CalendarView from './pages/SocialMedia/CalendarView';
import AccountCollegeTracker from './pages/SocialMedia/AccountCollegeTracker';
import StickyNotesBoard from './pages/StickyNotes/StickyNotesBoard';
import BrandManagement from './pages/BrandManagement';
import PermissionMatrix from './pages/PermissionMatrix';
import SocialMediaOptionsAdmin from './pages/admin/SocialMediaOptions';
import TaskOptionsAdmin from './pages/admin/TaskOptions';
import Homepage from './pages/Homepage';
import Layout from './components/Layout';
import BrandLogo from './components/BrandLogo';
import { Toaster } from './components/ui/sonner';
import './App.css';

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <BrandLogo size={64} className="rounded-2xl shadow-md mx-auto mb-5 animate-pulse" />
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
      <p className="mt-4 text-slate-600">Loading...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

/**
 * Consolidated role-aware route guard.
 * The single source of truth is the RBAC role's `page_access` map, delivered
 * via /api/rbac/users/{user_id}/permissions → PermissionsContext.
 * `permissionKey` is the page_access key (e.g. "social_media", "users").
 */
const RoleProtectedRoute = ({ children, permissionKey }) => {
  const { user, loading } = useAuth();
  const { hasPageAccess, isAdminRole, loaded } = usePermissions();

  if (loading || !loaded) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  const allowed = isAdminRole || (permissionKey ? hasPageAccess(permissionKey) : true);

  if (!allowed) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center" data-testid="access-denied">
          <div className="text-center">
            <div className="text-6xl mb-4">🚫</div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
            <p className="text-slate-600 mb-6">You don&apos;t have permission to access this page.</p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 btn-cta text-white rounded-lg hover:opacity-90 transition-opacity"
              data-testid="access-denied-back"
            >
              Go Back
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return <Layout>{children}</Layout>;
};

function App() {
  return (
    <AuthProvider>
      <PermissionsProvider>
        <WebSocketProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Homepage />} />
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/dashboard" element={<RoleProtectedRoute permissionKey="dashboard"><ManagerDashboard /></RoleProtectedRoute>} />
              <Route path="/my-day" element={<RoleProtectedRoute permissionKey="my_day"><MyDay /></RoleProtectedRoute>} />
              <Route path="/kanban" element={<RoleProtectedRoute permissionKey="kanban_board"><KanbanBoard /></RoleProtectedRoute>} />
              <Route path="/tasks" element={<RoleProtectedRoute permissionKey="task_table"><TaskTable /></RoleProtectedRoute>} />
              <Route path="/developer" element={<RoleProtectedRoute permissionKey="developer_queue"><DeveloperBoard /></RoleProtectedRoute>} />
              <Route path="/design-queue" element={<RoleProtectedRoute permissionKey="uiux_queue"><UIUXDesignQueue /></RoleProtectedRoute>} />
              <Route path="/manager-approval" element={<RoleProtectedRoute permissionKey="manager_approval"><ManagerApproval /></RoleProtectedRoute>} />
              <Route path="/user-types" element={<RoleProtectedRoute permissionKey="user_types"><UserTypesManagement /></RoleProtectedRoute>} />
              <Route path="/users" element={<RoleProtectedRoute permissionKey="users"><UserManagement /></RoleProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
              <Route path="/notes" element={<ProtectedRoute><StickyNotesBoard /></ProtectedRoute>} />
              <Route path="/activity-log" element={<RoleProtectedRoute permissionKey="activity_log"><ActivityLog /></RoleProtectedRoute>} />
              <Route path="/reports" element={<RoleProtectedRoute permissionKey="reports"><Reports /></RoleProtectedRoute>} />
              <Route path="/settings" element={<RoleProtectedRoute permissionKey="settings"><Settings /></RoleProtectedRoute>} />
              <Route path="/rbac-debug" element={<RoleProtectedRoute permissionKey="settings"><RBACDebug /></RoleProtectedRoute>} />
              <Route path="/admin/brands" element={<RoleProtectedRoute permissionKey="settings"><BrandManagement /></RoleProtectedRoute>} />
              <Route path="/admin/permissions" element={<RoleProtectedRoute permissionKey="settings"><PermissionMatrix /></RoleProtectedRoute>} />
              <Route path="/admin/social-media-options" element={<RoleProtectedRoute permissionKey="settings"><SocialMediaOptionsAdmin /></RoleProtectedRoute>} />
              <Route path="/admin/task-options" element={<RoleProtectedRoute permissionKey="settings"><TaskOptionsAdmin /></RoleProtectedRoute>} />

              {/* Social Media Module */}
              <Route path="/social-media" element={<RoleProtectedRoute permissionKey="social_media"><SocialMediaLayout /></RoleProtectedRoute>}>
                <Route index element={<SocialMediaDashboard />} />
                <Route path="posts" element={<PostsTable />} />
                <Route path="calendar" element={<CalendarView />} />
                <Route path="tracker" element={<AccountCollegeTracker />} />
              </Route>
            </Routes>
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </WebSocketProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
}

export default App;
